import React, { useState, useEffect } from "react";
import { Table, Typography, message, Spin, Button, Modal, Input, Select } from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";
import { format } from "date-fns";
import facturaApi from "../../../facturas/store/api";
import useResizeObserverErrorSuppression from "../../../../../hooks/useResizeObserverErrorSuppression";

const { Title, Text } = Typography;
const { TextArea } = Input;

const BillingLogTab = ({ client }) => {
  const [billingLogs, setBillingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Suppress ResizeObserver errors
  useResizeObserverErrorSuppression();

  useEffect(() => {
    if (client?.id) {
      fetchBillingLogs();
    }
  }, [client?.id]);

  const fetchBillingLogs = async (page = 1, pageSize = 20) => {
    if (!client?.id) return;

    try {
      setLoading(true);
      console.log("Fetching billing logs for client:", client.id, { page, pageSize });
      const response = await facturaApi.getBillingLogs(client.id, { page, pageSize });
      console.log("Admin billing logs response:", response);

      // Handle both old format (direct array) and new format (with data/meta)
      if (response.data && response.meta) {
        // New paginated format
        setBillingLogs(response.data);
        setPagination({
          current: response.meta.currentPage,
          pageSize: response.meta.itemsPerPage,
          total: response.meta.totalItems
        });
      } else {
        // Fallback to old format
        const logs = response.data || response || [];
        setBillingLogs(logs);
        setPagination((prev) => ({ ...prev, total: logs.length }));
      }
    } catch (error) {
      message.error("Failed to load billing logs");
      console.error("Error fetching billing logs:", error);
      setBillingLogs([]); // Set empty array on error
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationInfo) => {
    console.log("Admin table change:", paginationInfo);
    fetchBillingLogs(paginationInfo.current, paginationInfo.pageSize);
  };

  const handlePageChange = (page) => {
    fetchBillingLogs(page, pagination.pageSize);
  };

  const handlePageSizeChange = (size) => {
    fetchBillingLogs(1, size);
  };

  const renderCustomPagination = () => {
    const { current, pageSize, total } = pagination;
    const totalPages = Math.ceil(total / pageSize);
    const startItem = (current - 1) * pageSize + 1;
    const endItem = Math.min(current * pageSize, total);

    if (total === 0) return null;

    return (
      <div className="custom-pagination">
        <div className="pagination-info">
          <span>
            {startItem}-{endItem} of {total} entries
          </span>
        </div>

        <div className="pagination-controls">
          <Button
            disabled={current <= 1}
            onClick={() => handlePageChange(current - 1)}
            icon={<LeftOutlined />}
            size="small"
          />

          <div className="pagination-pages">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (current <= 3) {
                pageNum = i + 1;
              } else if (current >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = current - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  type={current === pageNum ? "primary" : "default"}
                  onClick={() => handlePageChange(pageNum)}
                  size="small"
                  className="page-button"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            disabled={current >= totalPages}
            onClick={() => handlePageChange(current + 1)}
            icon={<RightOutlined />}
            size="small"
          />
        </div>

        <div className="pagination-size">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            size="small"
            style={{ width: 80 }}
          >
            <Select.Option value={10}>10</Select.Option>
            <Select.Option value={20}>20</Select.Option>
            <Select.Option value={50}>50</Select.Option>
            <Select.Option value={100}>100</Select.Option>
          </Select>
          <span style={{ marginLeft: "8px" }}>/ page</span>
        </div>
      </div>
    );
  };

  const handleAddManualLog = () => {
    setIsModalVisible(true);
    setNote("");
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setNote("");
  };

  const handleModalOk = async () => {
    if (!note.trim()) {
      message.error("Please enter a note");
      return;
    }

    try {
      setIsSubmitting(true);
      await facturaApi.addManualBillingLog(client.id, note.trim());
      message.success("Manual log entry added successfully");
      setIsModalVisible(false);
      setNote("");
      // Refresh the logs
      fetchBillingLogs(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error adding manual log:", error);
      message.error("Failed to add manual log entry");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatEventType = (eventType) => {
    const eventMap = {
      "payment-method-add": "Payment Method Added",
      "payment-method-delete": "Payment Method Deleted",
      "payment-method-default-set": "Default Payment Method Changed",
      "payment-method-default-auto": "Default Payment Method Auto-Assigned",
      "payment-method-stripe-portal": "Stripe Portal Accessed",
      "entitlement-enabled": "Entitlement Enabled",
      "entitlement-disabled": "Entitlement Disabled",
      "billing-settings-change": "Billing Settings Changed",
      "manual-create": "Invoice Created",
      "manual-delete": "Invoice Deleted",
      "manual-note": "Note Added"
    };
    return eventMap[eventType] || eventType;
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp) => format(new Date(timestamp), "MMM dd, yyyy HH:mm"),
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      width: 160,
      fixed: "left"
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      render: formatEventType,
      width: 220
    },
    {
      title: "Description",
      dataIndex: "summary",
      key: "summary",
      width: 500,
      render: (text) => (
        <div
          style={{
            fontSize: "12px",
            lineHeight: "1.4",
            maxHeight: "60px",
            overflow: "hidden",
            wordWrap: "break-word",
            whiteSpace: "pre-wrap"
          }}
        >
          {text}
        </div>
      )
    },
    {
      title: "User",
      dataIndex: ["user", "name"],
      key: "user",
      render: (name, record) => name || record.user?.email || "System",
      width: 150
    },
    {
      title: "Invoice",
      dataIndex: ["factura", "facturaId"],
      key: "facturaId",
      render: (facturaId) => (facturaId ? <Text code>{facturaId}</Text> : "-"),
      width: 120
    }
  ];

  return (
    <div className="billing-log-tab">
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px"
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Billing Log
          </Title>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchBillingLogs(pagination.current, pagination.pageSize)}
              disabled={loading}
              title="Refresh logs"
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddManualLog}
              disabled={!client?.id}
            >
              Add Manual Log
            </Button>
          </div>
        </div>
        <Text type="secondary">
          Track all billing-related activities for {client?.clientName}, including payment
          methods, invoices, and system events.
        </Text>
      </div>

      <Spin spinning={loading}>
        <div style={{ overflowX: "auto" }} className="billing-log-table-container">
          <Table
            columns={columns}
            dataSource={billingLogs}
            rowKey="id"
            pagination={false}
            scroll={{ x: 1200, y: 400 }}
            size="middle"
            locale={{
              emptyText: loading
                ? "Loading..."
                : billingLogs.length === 0
                ? "No billing activity yet"
                : "No data"
            }}
          />
          {renderCustomPagination()}
        </div>
      </Spin>

      <Modal
        title="Add Manual Log Entry"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={isSubmitting}
        width={500}
      >
        <div style={{ marginBottom: "16px" }}>
          <Text strong>Client: </Text>
          <Text>{client?.clientName}</Text>
        </div>
        <div>
          <Text strong>Note:</Text>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter a note for this manual log entry..."
            rows={4}
            style={{ marginTop: "8px" }}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>

      <style>{`
        .billing-log-table-container .ant-table-tbody > tr > td {
          padding: 12px 8px;
          vertical-align: top;
        }

        .custom-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          margin-top: 16px;
          border-top: 1px solid #f0f0f0;
        }

        .pagination-info {
          color: rgba(0, 0, 0, 0.45);
          font-size: 14px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pagination-pages {
          display: flex;
          gap: 4px;
        }

        .pagination-pages .page-button {
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-size {
          display: flex;
          align-items: center;
          color: rgba(0, 0, 0, 0.45);
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .custom-pagination {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .pagination-controls {
            justify-content: center;
          }

          .pagination-info,
          .pagination-size {
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .pagination-pages {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default BillingLogTab;
